import Quill from 'quill';
import Delta from 'quill-delta';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useMarkdownConverter } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Card,
	Container,
	FormErrorMessage,
	GraphQLErrorMessage,
	Heading,
	Heading3,
	Paragraph,
	QuillInput,
} from '@treams/treams-ui';

export default function AISettingsContainer() {
	const { t } = useTranslation(undefined, { keyPrefix: 'settings.organization.ai' });
	const { t: tCommon } = useTranslation(undefined, { keyPrefix: 'common' });

	const { data, loading: isLoading } = graphql.useWorkspacePromptInstructionQuery({
		fetchPolicy: 'network-only',
	});
	const [update, { loading: isUpdating, error: mutationHookError }] =
		graphql.useSetWorkspacePromptInstructionMutation();

	const loading = isLoading || isUpdating;
	const instructions = data?.workspacePromptInstruction?.instructions;
	// Server-side (validation) errors carry `extensions.error`; anything else
	// (network failures, unexpected server errors) falls back to a generic message.
	const mutationGraphQLError = mutationHookError?.graphQLErrors?.[0];
	const hasServerErrorDetails = Boolean(mutationGraphQLError?.extensions?.error);

	const instructionsRef = useRef<Quill | undefined>();
	const { markdownToQuillDelta, htmlToMarkdown } = useMarkdownConverter();
	const [content, setContent] = useState<Delta | undefined>();

	useEffect(() => {
		const delta = markdownToQuillDelta(instructions ?? '');
		setContent(delta);
		instructionsRef.current?.setContents(delta, 'api');
	}, [instructions, markdownToQuillDelta]);

	const saveInstructions = useCallback(async () => {
		if (!instructionsRef.current) return;
		const html = instructionsRef.current.getSemanticHTML();
		const markdown = htmlToMarkdown(html);

		try {
			return await update({
				variables: {
					input: {
						instructions: markdown.trim(),
					},
				},
			});
		} catch {
			// Handled by mutationHookError
		}
	}, [htmlToMarkdown, update]);

	return (
		<Container>
			<Box>
				<Heading mb={2}>{t('title')}</Heading>
				<Paragraph>{t('description')}</Paragraph>
				<Card mt={4}>
					<Heading3 mb={1}>{t('organizationContext.label')}</Heading3>
					<Paragraph mb={3}>{t('organizationContext.explanation')}</Paragraph>
					{mutationHookError && (
						<Box mb={3}>
							{hasServerErrorDetails ? (
								<GraphQLErrorMessage
									error={mutationGraphQLError}
									showValidationDetails
									overrideFieldNames={{ instructions: t('organizationContext.label') }}
								/>
							) : (
								<FormErrorMessage error={mutationHookError} />
							)}
						</Box>
					)}
					{content && (
						<QuillInput
							onQuill={(quillEditor) => {
								instructionsRef.current = quillEditor;
							}}
							value={content}
							onChange={setContent}
							hasHeading
							hideLink
						/>
					)}
					<Flex justifyContent="right" mt={3}>
						<Button
							disabled={loading}
							loading={isUpdating}
							onClick={saveInstructions}
							title={tCommon('save')}
							small
						/>
					</Flex>
				</Card>
			</Box>
		</Container>
	);
}
